import { useCallback } from 'react';

import { useOrderContext } from './use-order-context.hook';

const DEEPLINK_URL_SEPARATOR = /,(?=[a-z][a-z0-9+.-]*:\/\/)/i;

export const splitDeeplinkUrls = (url: string): string[] =>
  url.split(DEEPLINK_URL_SEPARATOR);

export const useDeepLink = () => {
  const { order } = useOrderContext();

  const tryOpenDeepLink = useCallback(
    (url: string, timeoutMs: number = 300): Promise<boolean> =>
      new Promise((resolve) => {
        let resolved = false;

        const cleanup = () => {
          document.removeEventListener('visibilitychange', onVisibilityChange);
          window.removeEventListener('pagehide', handleSuccess);
        };

        const handleSuccess = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve(true);
          }
        };

        const onVisibilityChange = () => {
          if (document.visibilityState === 'hidden') {
            handleSuccess();
          }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', handleSuccess);

        window.location.href = url;

        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve(false);
          }
        }, timeoutMs);
      }),
    [],
  );

  const processDeeplinkUrl = useCallback(
    (url: string) =>
      url
        .replaceAll(
          '{{phone}}',
          order?.requisites?.phone?.replace(/\D/g, '') ?? '',
        )
        .replaceAll(
          '{{card}}',
          order?.requisites?.cardInfo?.replace(/\D/g, '') ?? '',
        )
        .replaceAll('{{amount}}', String(order?.amount || 0))
        .replaceAll(
          '{{payment_string}}',
          order?.requisites?.paymentString ?? '',
        )
        .replaceAll(
          '{{payment_string_vietnam_sber_qr}}',
          order?.requisites?.paymentStringVietnamSberQR ?? '',
        ),
    [order],
  );

  const openDeepLinks = useCallback(
    async (url: string) => {
      for (const deeplinkUrl of splitDeeplinkUrls(url)) {
        if (await tryOpenDeepLink(deeplinkUrl.trim())) {
          return;
        }
      }
    },
    [tryOpenDeepLink],
  );

  return {
    processDeeplinkUrl,
    openDeepLinks,
    tryOpenDeepLink,
  };
};
